import React, { useState, useEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import RideCard from '../components/RideCard';
import RideCardSkeleton from '../components/RideCardSkeleton';
import { useLang } from '../context/LanguageContext';
import { getNewRides, getMyDriverRides, acceptRide, arriveRide, startRide, completeRide, cancelRide, setOnlineStatus, updateDriverLocation, rateClient, updateVehicleLocation, updateVehicleDetails, getMyVehicle, getTariffs } from '../services/api';
import { useRideEvents } from '../hooks/useRideEvents';

function getGreeting(g) {
  const h = new Date().getHours();
  if (h < 12) return g.morning;
  if (h < 18) return g.afternoon;
  return g.evening;
}

export default function DriverPage({ user, onBack }) {
  const [newRides, setNewRides]         = useState([]);
  const [myRides, setMyRides]           = useState([]);
  const [isOnline, setIsOnline]         = useState(false);
  const [initialLoad, setInitialLoad]   = useState(true);
  const [historyPage, setHistoryPage]   = useState(0);
  const [hasMoreHistory, setHasMoreHistory] = useState(false);
  const [loadingMore, setLoadingMore]   = useState(false);
  const [totalMyRides, setTotalMyRides] = useState(0);
  const [vehicle, setVehicle]           = useState({ model: '', color: '', plateNumber: '', vehicleClass: '' });
  const [vehicleSaving, setVehicleSaving] = useState(false);
  const [tariffs, setTariffs]           = useState([]);
  const [showGeoConsent, setShowGeoConsent] = useState(false);
  const { t } = useLang();
  const d = t.driver;
  const prevNewRidesCountRef = useRef(-1);
  const historyPageRef = useRef(0);
  const GEO_CONSENT_KEY = 'haytaxi-geo-consent';

  useEffect(() => {
    getMyVehicle()
      .then((res) => setVehicle({
        model: res.data.model || '',
        color: res.data.color || '',
        plateNumber: res.data.plateNumber || '',
        vehicleClass: res.data.vehicleClass || '',
      }))
      .catch(() => {});
  }, []);

  useEffect(() => {
    getTariffs().then((res) => setTariffs(res.data)).catch(() => {});
  }, []);

  const handleVehicleChange = (field) => (e) => {
    setVehicle((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSaveVehicle = async () => {
    setVehicleSaving(true);
    try {
      await updateVehicleDetails(vehicle.model, vehicle.color, vehicle.plateNumber, vehicle.vehicleClass || undefined);
      toast.success(d.vehicleSaved);
    } catch {
      toast.error(d.vehicleSaveError);
    } finally {
      setVehicleSaving(false);
    }
  };

  const fetchRides = useCallback(async () => {
    try {
      const [newRes, myRes] = await Promise.all([getNewRides(), getMyDriverRides(0)]);
      const incoming = newRes.data;

      if (prevNewRidesCountRef.current >= 0 && incoming.length > prevNewRidesCountRef.current) {
        toast.success(d.notifyNewRide);
        // Toast is invisible while the tab is hidden/unfocused - a system
        // notification covers that case so the driver doesn't miss a ride.
        if (document.hidden && 'Notification' in window && Notification.permission === 'granted') {
          const notification = new Notification(d.notifyNewRide);
          notification.onclick = () => { window.focus(); notification.close(); };
        }
      }
      prevNewRidesCountRef.current = incoming.length;

      setNewRides(incoming);
      setMyRides(myRes.data.content);
      setHasMoreHistory(!myRes.data.last);
      setTotalMyRides(myRes.data.totalElements);
      setHistoryPage(0);
      historyPageRef.current = 0;
    } catch (err) {
      console.error('Failed to fetch rides:', err);
    } finally {
      setInitialLoad(false);
    }
  }, [user.id, d]);

  useEffect(() => { fetchRides(); }, [fetchRides]);

  const refreshFromSSE = useCallback(async () => {
    try {
      const [newRes, myRes] = await Promise.all([getNewRides(), getMyDriverRides(0)]);
      const incoming = newRes.data;
      if (prevNewRidesCountRef.current >= 0 && incoming.length > prevNewRidesCountRef.current) {
        toast.success(d.notifyNewRide);
        if (document.hidden && 'Notification' in window && Notification.permission === 'granted') {
          const notification = new Notification(d.notifyNewRide);
          notification.onclick = () => { window.focus(); notification.close(); };
        }
      }
      prevNewRidesCountRef.current = incoming.length;
      setNewRides(incoming);
      const historyResponses = await Promise.all(
        Array.from({ length: historyPageRef.current + 1 }, (_, i) => getMyDriverRides(i))
      );
      const fresh = historyResponses.flatMap((res) => res.data.content);
      setMyRides(fresh);
    } catch (err) {
      console.error('Failed to refresh rides:', err);
    }
  }, [user.id, d]);

  useRideEvents(refreshFromSSE);

  const handleAccept = async (rideId) => {
    try {
      await acceptRide(rideId);
      await fetchRides();
    } catch {
      toast.error(d.acceptError);
    }
  };

  const handleLoadMoreHistory = async () => {
    setLoadingMore(true);
    try {
      const nextPage = historyPage + 1;
      const res = await getMyDriverRides(nextPage);
      setMyRides((prev) => [...prev, ...res.data.content]);
      setHasMoreHistory(!res.data.last);
      setHistoryPage(nextPage);
      historyPageRef.current = nextPage;
    } catch (err) {
      console.error('Failed to load more history:', err);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleArrive = async (rideId, version) => {
    try {
      await arriveRide(rideId, version);
      await fetchRides();
    } catch {
      toast.error(d.arriveError || 'Could not update ride');
    }
  };

  const handleStart = async (rideId, version) => {
    try {
      await startRide(rideId, version);
      await fetchRides();
    } catch {
      toast.error(d.startError || 'Could not update ride');
    }
  };

  const handleComplete = async (rideId, version) => {
    try {
      await completeRide(rideId, version);
      await fetchRides();
    } catch {
      toast.error(d.completeError);
    }
  };

  const handleCancel = async (rideId) => {
    try {
      await cancelRide(rideId);
      await fetchRides();
    } catch {
      toast.error(d.cancelError);
    }
  };

  const handleRateClient = async (rideId, rating, review) => {
    try {
      await rateClient(rideId, rating, review);
      await fetchRides();
    } catch {
      toast.error(d.rateClientError || 'Could not submit rating');
    }
  };

  const goOnline = async () => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
    try {
      await setOnlineStatus(true);
      setIsOnline(true);
      await fetchRides();
    } catch {
      toast.error(d.onlineError || 'Could not update online status');
    }
  };

  const handleToggleOnline = async () => {
    if (isOnline) {
      try {
        await setOnlineStatus(false);
        setIsOnline(false);
        await fetchRides();
      } catch {
        toast.error(d.onlineError || 'Could not update online status');
      }
      return;
    }

    let hasGeoConsent = false;
    try {
      hasGeoConsent = localStorage.getItem(GEO_CONSENT_KEY) === 'true';
    } catch { /* ignore */ }

    if (hasGeoConsent) {
      await goOnline();
    } else {
      setShowGeoConsent(true);
    }
  };

  const handleGeoConsentAccept = async () => {
    try { localStorage.setItem(GEO_CONSENT_KEY, 'true'); } catch { /* ignore */ }
    setShowGeoConsent(false);
    await goOnline();
  };

  const earnings = myRides
    .filter((r) => r.status === 'COMPLETED')
    .reduce((sum, r) => sum + (r.price || 0), 0);

  const ACTIVE_STATUSES = ['ACCEPTED', 'ARRIVED', 'IN_PROGRESS'];
  const currentRide = myRides.find((r) => ACTIVE_STATUSES.includes(r.status));

  // Tracks the active ride without resubscribing watchPosition on every ride change -
  // the GPS watch below only needs to start/stop with online status.
  const currentRideRef = useRef(null);
  useEffect(() => { currentRideRef.current = currentRide; }, [currentRide]);

  const watchIdRef = useRef(null);
  const lastPosRef = useRef(null);
  useEffect(() => {
    if (!isOnline || !navigator.geolocation) return;
    lastPosRef.current = null;
    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        const { latitude: lat, longitude: lng } = pos.coords;
        const last = lastPosRef.current;
        if (last) {
          const dLat = (lat - last.lat) * Math.PI / 180;
          const dLng = (lng - last.lng) * Math.PI / 180;
          const a = Math.sin(dLat / 2) ** 2
            + Math.cos(last.lat * Math.PI / 180) * Math.cos(lat * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
          const distKm = 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
          if (distKm < 0.05) return;
        }
        lastPosRef.current = { lat, lng };
        updateVehicleLocation(lat, lng).catch(() => {});
        const ride = currentRideRef.current;
        if (ride) {
          updateDriverLocation(ride.id, lat, lng).catch(() => {});
        }
      },
      () => { toast(d.geoError, { icon: '⚠️' }); },
      { enableHighAccuracy: true, maximumAge: 5000 }
    );
    return () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
    };
  }, [isOnline]);

  // Keeps the screen awake while online so watchPosition keeps running in the
  // foreground - most mobile browsers throttle/suspend JS once the screen sleeps.
  useEffect(() => {
    if (!isOnline || !('wakeLock' in navigator)) return;

    let cancelled = false;
    let wakeLock = null;

    const requestWakeLock = async () => {
      try {
        wakeLock = await navigator.wakeLock.request('screen');
      } catch { /* not critical - tracking still runs, screen may just sleep */ }
    };
    requestWakeLock();

    const handleVisibilityChange = () => {
      if (!cancelled && document.visibilityState === 'visible') requestWakeLock();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      wakeLock?.release().catch(() => {});
    };
  }, [isOnline]);

  return (
    <div className="bg-slate-900 min-h-screen">
    <div className="max-w-6xl mx-auto px-6 py-10">

      {/* ── Header ── */}
      <div className="mb-8">
        <button onClick={onBack} className="flex items-center gap-1 text-sm text-white/40 hover:text-white mb-3 transition-colors">{d.back}</button>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <p className="text-sm text-white/40 mb-0.5">{getGreeting(t.greeting)},</p>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-black text-white">{user.name}</h1>
              <button
                onClick={handleToggleOnline}
                className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full transition-colors ${
                  isOnline
                    ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30 border border-green-500/30'
                    : 'bg-white/10 text-white/50 hover:bg-white/20 border border-white/20'
                }`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${isOnline ? 'bg-green-400 animate-pulse' : 'bg-white/40'}`} />
                {isOnline ? d.online : (d.offline || 'Offline')}
              </button>
            </div>
            <p className="text-white/50 text-sm mt-0.5">{d.subtitle}</p>
          </div>
        </div>
      </div>

      {/* ── Stats ── */}
      <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-8">
        <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl text-center py-3 sm:py-4 px-4">
          <div className="text-xl sm:text-2xl font-black text-white">{totalMyRides}</div>
          <div className="text-xs text-white/40 mt-0.5">{d.totalRides}</div>
        </div>
        <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl text-center py-3 sm:py-4 px-4">
          <div className="text-xl sm:text-2xl font-black text-primary-400">€{earnings.toFixed(2)}</div>
          <div className="text-xs text-white/40 mt-0.5">{d.earned}</div>
        </div>
        <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl text-center py-3 sm:py-4 px-4">
          <div className="text-xl sm:text-2xl font-black text-blue-400">{newRides.length}</div>
          <div className="text-xs text-white/40 mt-0.5">{d.available}</div>
        </div>
      </div>

      {/* ── Vehicle details ── */}
      <div className="mb-8 bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-4 sm:p-5">
        <h2 className="text-sm font-semibold text-white/70 mb-3">{d.vehicleTitle}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <input
            type="text" value={vehicle.model} onChange={handleVehicleChange('model')}
            placeholder={d.vehicleModel}
            className="bg-white/10 border border-white/20 rounded-xl px-3 py-2 text-sm text-white placeholder-white/30 focus:outline-none focus:border-white/40"
          />
          <input
            type="text" value={vehicle.color} onChange={handleVehicleChange('color')}
            placeholder={d.vehicleColor}
            className="bg-white/10 border border-white/20 rounded-xl px-3 py-2 text-sm text-white placeholder-white/30 focus:outline-none focus:border-white/40"
          />
          <input
            type="text" value={vehicle.plateNumber} onChange={handleVehicleChange('plateNumber')}
            placeholder={d.vehiclePlate}
            className="bg-white/10 border border-white/20 rounded-xl px-3 py-2 text-sm text-white placeholder-white/30 focus:outline-none focus:border-white/40"
          />
          <select
            value={vehicle.vehicleClass} onChange={handleVehicleChange('vehicleClass')}
            className="bg-white/10 border border-white/20 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-white/40"
          >
            <option value="" className="bg-slate-800">{d.vehicleClassPlaceholder}</option>
            {tariffs.map((tariff) => (
              <option key={tariff.code} value={tariff.code} className="bg-slate-800">{tariff.name}</option>
            ))}
          </select>
        </div>
        <button
          onClick={handleSaveVehicle}
          disabled={vehicleSaving}
          className="mt-3 text-sm font-semibold px-5 py-2 rounded-full bg-primary-600 text-white hover:bg-primary-700 transition-colors disabled:opacity-50"
        >
          {vehicleSaving ? '…' : d.vehicleSave}
        </button>
      </div>

      {/* ── Current ride ── */}
      {currentRide && (
        <div className="mb-8">
          <h2 className="text-sm font-semibold text-amber-400 uppercase tracking-wider mb-4">{d.currentRide || 'Current ride'}</h2>
          <div className="max-w-sm">
            <RideCard ride={currentRide} showActions onArrive={handleArrive} onStart={handleStart} onComplete={handleComplete} onCancel={handleCancel} />
          </div>
        </div>
      )}

      {/* ── Available rides ── */}
      <div className="mb-10">
        <div className="flex items-center gap-3 mb-5">
          <h2 className="text-lg font-black text-white">{d.availableTitle}</h2>
          {newRides.length > 0 && (
            <span className="bg-primary-600 text-white text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center">{newRides.length}</span>
          )}
        </div>
        {initialLoad ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => <RideCardSkeleton key={i} />)}
          </div>
        ) : newRides.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-40 border-2 border-dashed border-white/20 rounded-2xl text-center">
            <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center mb-3">
              {isOnline ? (
                <svg className="w-5 h-5 text-white/40" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
                </svg>
              ) : (
                <svg className="w-5 h-5 text-white/40" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5.636 5.636a9 9 0 1012.728 12.728M5.636 5.636A9 9 0 0118.364 18.364M5.636 5.636L18.364 18.364" />
                </svg>
              )}
            </div>
            {isOnline ? (
              <>
                <p className="text-white/50 font-medium text-sm">{d.noRides}</p>
                <p className="text-white/30 text-xs mt-0.5">{d.noRidesHint}</p>
              </>
            ) : (
              <>
                <p className="text-white/50 font-medium text-sm">{d.offlineMsg}</p>
                <p className="text-white/30 text-xs mt-0.5">{d.offlineHint}</p>
              </>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {newRides.map((ride) => <RideCard key={ride.id} ride={ride} showActions onAccept={handleAccept} />)}
          </div>
        )}
      </div>

      {/* ── My rides history ── */}
      {myRides.filter((r) => !ACTIVE_STATUSES.includes(r.status)).length > 0 && (
        <div>
          <h2 className="text-lg font-black text-white mb-5">{d.myRides}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {[...myRides].filter((r) => !ACTIVE_STATUSES.includes(r.status)).reverse().map((ride) => (
              <RideCard key={ride.id} ride={ride} showActions onComplete={handleComplete} onRateClient={handleRateClient} />
            ))}
          </div>
          {hasMoreHistory && (
            <div className="text-center mt-6">
              <button
                onClick={handleLoadMoreHistory}
                disabled={loadingMore}
                className="text-sm font-semibold px-6 py-2.5 rounded-full border-2 border-white/20 text-white/60 hover:border-white/40 hover:text-white transition-colors disabled:opacity-50"
              >
                {loadingMore ? '...' : d.loadMore}
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Geolocation consent gate ── */}
      {showGeoConsent && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-xl">
            <p className="font-semibold text-slate-800 mb-2">{d.geoConsentTitle}</p>
            <p className="text-sm text-slate-500 mb-5">{d.geoConsentText}</p>
            <div className="flex gap-3">
              <button
                onClick={handleGeoConsentAccept}
                className="flex-1 py-2.5 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 transition-colors"
              >
                {d.geoConsentAccept}
              </button>
              <button
                onClick={() => setShowGeoConsent(false)}
                className="flex-1 py-2.5 rounded-full border-2 border-slate-200 text-slate-600 text-sm font-semibold hover:border-slate-400 transition-colors"
              >
                {d.geoConsentDecline}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </div>
  );
}
